'use client';

import React, { forwardRef } from 'react';
import Link from 'next/link';
import { Icon, type IconName } from '../../icons';
import './NotificationItem.css';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface NotificationItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Notification type (determines icon color) */
  type?: NotificationType;
  /** Custom icon (overrides default based on type) */
  icon?: IconName;
  /** Notification title */
  title: string;
  /** Notification message */
  message?: string;
  /** Date/time of the notification */
  date?: string;
  /** Whether the notification is unread */
  unread?: boolean;
  /** Action URL (renders View button) */
  actionUrl?: string;
  /** Action label (default: "View") */
  actionLabel?: string;
  /** Custom action element */
  action?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
}

const getDefaultIcon = (type: NotificationType): IconName => {
  switch (type) {
    case 'success':
      return 'Check';
    case 'warning':
      return 'AlertTriangle';
    case 'error':
      return 'AlertCircle';
    default:
      return 'Info';
  }
};

const NotificationItem = forwardRef<HTMLDivElement, NotificationItemProps>(
  (
    {
      type = 'info',
      icon,
      title,
      message,
      date,
      unread = false,
      actionUrl,
      actionLabel = 'View',
      action,
      onClick,
      className = '',
      ...props
    },
    ref
  ) => {
    const displayIcon = icon || getDefaultIcon(type);

    return (
      <div
        ref={ref}
        className={`ds-notification-item ${onClick ? 'ds-notification-item--clickable' : ''} ${unread ? 'ds-notification-item--unread' : ''} ${className}`}
        onClick={onClick}
        onKeyDown={(event) => {
          if (!onClick || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          onClick();
        }}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={
          onClick
            ? `${unread ? 'Unread notification: ' : ''}${title}${message ? `. ${message}` : ''}`
            : undefined
        }
        {...props}
      >
        <div className={`ds-notification-item__icon ds-notification-item__icon--${type}`}>
          <Icon name={displayIcon} size={16} />
        </div>
        <div className="ds-notification-item__content">
          <p className="ds-notification-item__title">{title}</p>
          {message && <p className="ds-notification-item__message">{message}</p>}
          {date && <span className="ds-notification-item__date">{date}</span>}
        </div>
        {unread ? (
          <span className="ds-notification-item__unread-dot" aria-hidden="true" />
        ) : null}
        {(actionUrl || action) && (
          <div className="ds-notification-item__action">
            {action || (
              <Link href={actionUrl!} className="ds-notification-item__action-link">
                {actionLabel}
              </Link>
            )}
          </div>
        )}
      </div>
    );
  }
);

NotificationItem.displayName = 'NotificationItem';

// NotificationList container
export interface NotificationListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const NotificationList = forwardRef<HTMLDivElement, NotificationListProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div ref={ref} className={`ds-notification-list ${className}`} {...props}>
        {children}
      </div>
    );
  }
);

NotificationList.displayName = 'NotificationList';

export { NotificationItem, NotificationList };
